import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiHeaders } from '../../utils/apiHelpers';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  FileSearch, Upload, Loader2, Search, X, Calendar, FileText, PieChart,
  Truck, Package, Building2, Eye, ChevronUp, ChevronDown, Sparkles
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TenantFattureElettroniche = ({ tenantDb }) => {
  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [view, setView] = useState('anni'); // anni, list, fornitori, prodotti, categorie
  const [statisticheFornitori, setStatisticheFornitori] = useState([]);
  const [statisticheProdotti, setStatisticheProdotti] = useState([]);
  const [statisticheAnni, setStatisticheAnni] = useState([]);
  const [statisticheCategorie, setStatisticheCategorie] = useState([]);
  const [selectedAnno, setSelectedAnno] = useState(new Date().getFullYear());
  const [filters, setFilters] = useState({
    fornitore: '',
    categoria: '',
    sottocategoria: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFornitori, setExpandedFornitori] = useState({});
  const [expandedCategorie, setExpandedCategorie] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  // Stato per ricerca articoli
  const [searchArticoli, setSearchArticoli] = useState('');
  const [articoliTrovati, setArticoliTrovati] = useState([]);
  const [loadingArticoli, setLoadingArticoli] = useState(false);
  
  // Stato per modal risultati upload
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Cache per i dati già caricati
  const dataCache = useRef({});
  
  // Helper per headers con tenant DB - usa getApiHeaders centralizzato
  const getHeaders = useCallback(() => {
    return getApiHeaders();
  }, []);

  // Genera chiave cache
  const getCacheKey = useCallback((viewType, anno) => {
    return `${viewType}_${anno}`;
  }, []);
  
  // Load data on mount
  useEffect(() => {
    loadStatisticheAnni();
  }, []);
  
  // Load data when year or view changes
  useEffect(() => {
    if (view === 'anni') {
      setLoading(false);
      return;
    }
    
    const hasActiveFilters = filters.categoria || filters.sottocategoria || filters.fornitore;
    const cacheKey = getCacheKey(view, selectedAnno);
    
    if (!hasActiveFilters && dataCache.current[cacheKey]) {
      const cached = dataCache.current[cacheKey];
      if (view === 'list') setFatture(cached);
      else if (view === 'fornitori') setStatisticheFornitori(cached);
      else if (view === 'prodotti') setStatisticheProdotti(cached);
      else if (view === 'categorie') setStatisticheCategorie(cached);
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      if (view === 'list') await loadFattureAnno();
      else if (view === 'fornitori') await loadStatisticheFornitori();
      else if (view === 'prodotti') await loadStatisticheProdotti();
      else if (view === 'categorie') await loadStatisticheCategorie();
    };
    
    loadData();
  }, [view, selectedAnno, getCacheKey, filters.categoria, filters.sottocategoria, filters.fornitore]);
  
  // Categorizza fatture - processa in batch per evitare timeout
  const categorizzaFatture = async () => {
    setCategorizing(true);
    let totaleElaborate = 0;
    let continuaElaborazione = true;
    
    try {
      while (continuaElaborazione) {
        const response = await axios.post(`${API}/gestione/fatture/categorizza-tutte`, {}, { headers: getHeaders() });
        const { categorizzate, rimanenti, messaggio } = response.data;
        
        totaleElaborate += categorizzate || 0;
        
        // Mostra progresso
        if (rimanenti > 0) {
          toast.info(`⏳ Elaborate ${totaleElaborate} fatture... (${rimanenti} rimanenti)`);
        } else {
          continuaElaborazione = false;
          toast.success(`✅ ${messaggio || 'Categorizzazione completata!'}`);
        }
        
        // Se non ci sono più fatture da elaborare o non ne ha elaborate nessuna, esci
        if (rimanenti === 0 || categorizzate === 0) {
          continuaElaborazione = false;
        }
      }
      
      await loadStatisticheAnni();
      if (view === 'categorie') await loadStatisticheCategorie();
      if (view === 'list') await loadFattureAnno();
    } catch (error) {
      console.error('Error categorizing:', error);
      toast.error('Errore categorizzazione: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCategorizing(false);
    }
  };
  
  const loadStatisticheAnni = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/anni`, { headers: getHeaders() });
      setStatisticheAnni(response.data.anni || []);
      if (response.data.anni?.length > 0) {
        setSelectedAnno(response.data.anni[0].anno);
      }
    } catch (error) {
      console.error('Error loading statistiche anni:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadFattureAnno = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.fornitore) params.append('fornitore', filters.fornitore);
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.sottocategoria) params.append('sottocategoria', filters.sottocategoria);
      
      const response = await axios.get(`${API}/gestione/fatture/anno/${selectedAnno}?${params.toString()}`, { headers: getHeaders() });
      const loadedFatture = response.data.fatture || [];
      setFatture(loadedFatture);
      
      const cacheKey = getCacheKey('list', selectedAnno);
      dataCache.current[cacheKey] = loadedFatture;
    } catch (error) {
      console.error('Error loading fatture:', error);
      
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheFornitori = async () => {
    setLoading(true);
    try {
      const startDate = `${selectedAnno}-01-01`;
      const endDate = `${selectedAnno}-12-31`;
      
      const response = await axios.get(`${API}/gestione/fatture/statistiche/fornitori?data_inizio=${startDate}&data_fine=${endDate}`, { headers: getHeaders() });
      const loadedFornitori = response.data.fornitori || [];
      setStatisticheFornitori(loadedFornitori);
      
      const cacheKey = getCacheKey('fornitori', selectedAnno);
      dataCache.current[cacheKey] = loadedFornitori;
    } catch (error) {
      console.error('Error loading statistiche fornitori:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheProdotti = async () => {
    setLoading(true);
    try {
      const startDate = `${selectedAnno}-01-01`;
      const endDate = `${selectedAnno}-12-31`;
      
      const response = await axios.get(`${API}/gestione/fatture/statistiche/prodotti?data_inizio=${startDate}&data_fine=${endDate}`, { headers: getHeaders() });
      const loadedProdotti = response.data.prodotti || [];
      setStatisticheProdotti(loadedProdotti);
      
      const cacheKey = getCacheKey('prodotti', selectedAnno);
      dataCache.current[cacheKey] = loadedProdotti;
    } catch (error) {
      console.error('Error loading statistiche prodotti:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheCategorie = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/categorie?anno=${selectedAnno}`, { headers: getHeaders() });
      const loadedCategorie = response.data.categorie || [];
      setStatisticheCategorie(loadedCategorie);
      
      const cacheKey = getCacheKey('categorie', selectedAnno);
      dataCache.current[cacheKey] = loadedCategorie;
    } catch (error) {
      console.error('Error loading statistiche categorie:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Upload fatture XML/ZIP
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axios.post(`${API}/gestione/fatture/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...getHeaders()
        },
        timeout: 300000 // 5 minuti per file grandi
      });
      
      // Mostra sempre il modal con i risultati dettagliati
      setUploadResults(response.data);
      setShowUploadResults(true);
      
      if (response.data.caricati > 0) {
        await loadStatisticheAnni();
        if (view === 'list') await loadFattureAnno();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      // Mostra errore nel modal
      setUploadResults({
        success: false,
        totale_file: files.length,
        caricati: 0,
        duplicati: 0,
        errori_dettagli: [error.response?.data?.detail || error.message || 'Errore di connessione'],
        message: 'Errore durante il caricamento'
      });
      setShowUploadResults(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Drag & Drop handlers
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files); };
  
  const viewFatturaDetail = async (fatturaId) => {
    try {
      const response = await axios.get(`${API}/gestione/fatture/${fatturaId}`, { headers: getHeaders() });
      setSelectedFattura(response.data);
      setShowDetail(true);
    } catch (error) {
      
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('it-IT'); } 
    catch { return dateStr; }
  };
  
  // Cerca articoli nelle fatture
  const cercaArticoli = async (query) => {
    if (!query || query.length < 2) { setArticoliTrovati([]); return; }
    
    setLoadingArticoli(true);
    try {
      // Usa il nuovo endpoint con logica migliorata
      const response = await axios.get(`${API}/gestione/fatture/prodotti/cerca`, {
        params: { search: query, limit: 50 },
        headers: getHeaders()
      });
      setArticoliTrovati(response.data.prodotti || []);
    } catch (error) {
      console.error('Errore ricerca articoli:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setLoadingArticoli(false);
    }
  };
  
  // Filter fatture by search term
  const filteredFatture = fatture.filter(f => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      f.fornitore?.denominazione?.toLowerCase().includes(search) ||
      f.fornitore?.toLowerCase?.().includes(search) ||
      f.numero_fattura?.toLowerCase().includes(search) ||
      f.numero?.toLowerCase?.().includes(search)
    );
  });
  
  // Parse data fattura (ISO o italiana) -> Date
  const parseDataFattura = (s) => {
    if (!s) return null;
    const str = String(s);
    if (str.includes('-') && str.length >= 10) return new Date(str.slice(0, 10));
    if (str.includes('/')) {
      const [g, m, a] = str.split('/');
      if (a) return new Date(`${a}-${m}-${g}`);
    }
    return null;
  };
  
  // Fatture ordinate per data (più recente prima) e raggruppate per mese
  const MESI_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const fattureMensili = (() => {
    const sorted = [...filteredFatture].sort((a, b) =>
      (parseDataFattura(b.data || b.data_fattura)?.getTime() || 0) - (parseDataFattura(a.data || a.data_fattura)?.getTime() || 0)
    );
    const gruppi = [];
    let corrente = null;
    for (const f of sorted) {
      const d = parseDataFattura(f.data || f.data_fattura);
      const chiave = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'senza-data';
      if (!corrente || corrente.chiave !== chiave) {
        corrente = {
          chiave,
          label: d ? `${MESI_IT[d.getMonth()]} ${d.getFullYear()}` : 'Senza data',
          items: [],
          totale: 0
        };
        gruppi.push(corrente);
      }
      corrente.items.push(f);
      corrente.totale += (f.importo_totale || f.importo || 0);
    }
    return gruppi;
  })();
  
  // Toggle expand for fornitori
  const toggleFornitore = (fornitore) => {
    setExpandedFornitori(prev => ({ ...prev, [fornitore]: !prev[fornitore] }));
  };

  // Colori categorie
  const getCatColor = (cat) => {
    const colors = {
      'Food & Materie Prime': { bg: 'bg-emerald-500', text: 'text-emerald-300 lg:text-emerald-600', border: 'border-emerald-500/30', icon: '🍽️' },
      'Alimentari': { bg: 'bg-emerald-500', text: 'text-emerald-300 lg:text-emerald-600', border: 'border-emerald-500/30', icon: '🍽️' },
      'Bevande': { bg: 'bg-blue-500', text: 'text-blue-300 lg:text-blue-600', border: 'border-blue-500/30', icon: '🍷' },
      'Pulizia & Igiene': { bg: 'bg-cyan-500', text: 'text-cyan-300 lg:text-cyan-600', border: 'border-cyan-500/30', icon: '🧹' },
      'Attrezzature': { bg: 'bg-amber-500', text: 'text-amber-300 lg:text-amber-600', border: 'border-amber-500/30', icon: '🔧' },
      'Servizi': { bg: 'bg-purple-500', text: 'text-purple-300 lg:text-purple-600', border: 'border-purple-500/30', icon: '👔' },
      'Utenze': { bg: 'bg-yellow-500', text: 'text-yellow-300 lg:text-yellow-600', border: 'border-yellow-500/30', icon: '💡' },
      'Marketing': { bg: 'bg-pink-500', text: 'text-pink-300 lg:text-pink-600', border: 'border-pink-500/30', icon: '📢' },
      'Altro': { bg: 'bg-slate-400', text: 'text-slate-300 lg:text-gray-500', border: 'border-slate-400/30', icon: '📋' }
    };
    return colors[cat] || { bg: 'bg-slate-500', text: 'text-slate-300 lg:text-gray-600', border: 'border-slate-500/30', icon: '📋' };
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white lg:text-gray-900 flex items-center gap-2">
              <FileSearch className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-cyan-400 lg:text-cyan-600" />
              Fatture Elettroniche
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 lg:text-gray-600 mt-1">
            Importa e analizza le fatture passive
          </p>
        </div>
        
        {/* Buttons */}
        <div className="grid grid-cols-2 sm:flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(e.target.files)}
            accept=".xml,.p7m,.zip"
            multiple
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-green-600 hover:bg-green-700 text-sm px-3 py-2"
            size="sm"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /><span className="hidden sm:inline">Caricando...</span></>
            ) : (
              <><Upload className="h-4 w-4 mr-1" /><span>Carica</span></>
            )}
          </Button>
          <Button
            onClick={categorizzaFatture}
            disabled={categorizing}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-2"
          >
            {categorizing ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /><span className="hidden sm:inline">AI...</span></>
            ) : (
              <><PieChart className="h-4 w-4 mr-1" /><span>AI Cat.</span></>
            )}
          </Button>
        </div>
      </div>
      
      {/* Barra Ricerca Articoli */}
      <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl shadow-sm border border-slate-700/50 lg:border-gray-100 p-3 lg:p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
            <Input
              placeholder="Cerca articoli..."
              value={searchArticoli}
              onChange={(e) => setSearchArticoli(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') cercaArticoli(searchArticoli); }}
              className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400 h-10"
            />
          </div>
          <Button
            onClick={() => cercaArticoli(searchArticoli)}
            disabled={loadingArticoli || searchArticoli.length < 2}
            className="bg-cyan-600 hover:bg-cyan-700 px-3"
            size="sm"
          >
            {loadingArticoli ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
          {articoliTrovati.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setArticoliTrovati([]); setSearchArticoli(''); }}
              className="border-slate-600 lg:border-gray-200 text-slate-300 lg:text-gray-900 px-3"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Risultati Ricerca Prodotti */}
        {articoliTrovati.length > 0 && (
          <div className="mt-3 border-t border-slate-700/50 lg:border-gray-100 pt-3">
            <p className="text-xs font-medium text-slate-400 lg:text-gray-700 mb-2">
              {articoliTrovati.length} prodotti trovati per &quot;{searchArticoli}&quot;
            </p>
            <div className="max-h-96 overflow-y-auto space-y-3">
              {articoliTrovati.map((gruppo, idx) => (
                <div key={idx} className="bg-slate-700/50 lg:bg-gray-50 rounded-xl lg:rounded-lg overflow-hidden border border-slate-600/50 lg:border-gray-200">
                  {/* Header Gruppo */}
                  <div className="p-3 border-b border-slate-600/30 lg:border-gray-200 bg-slate-800/50 lg:bg-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-white lg:text-gray-900">{gruppo.nome_prodotto}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <span className="text-green-400 lg:text-green-600 font-bold">€{(gruppo.prezzo_min || 0).toFixed(2)}/pz</span>
                            {gruppo.fornitore_min && (
                              <span className="text-green-300 lg:text-green-500 text-xs">({gruppo.fornitore_min.split(' ')[0]})</span>
                            )}
                          </div>
                          {gruppo.prezzo_max > gruppo.prezzo_min && (
                            <>
                              <span className="text-slate-500 lg:text-gray-400">→</span>
                              <div className="flex items-center gap-1">
                                <span className="text-red-400 lg:text-red-500">€{(gruppo.prezzo_max || 0).toFixed(2)}/pz</span>
                                {gruppo.fornitore_max && (
                                  <span className="text-red-300 lg:text-red-400 text-xs">({gruppo.fornitore_max.split(' ')[0]})</span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {gruppo.num_fornitori > 1 && (
                        <span className="bg-purple-500/20 lg:bg-purple-100 text-purple-300 lg:text-purple-700 px-2 py-1 rounded text-xs font-medium">
                          {gruppo.num_fornitori} fornitori
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Lista Occorrenze */}
                  <div className="divide-y divide-slate-600/30 lg:divide-gray-100">
                    {(gruppo.occorrenze || []).slice(0, 5).map((occ, occIdx) => (
                      <div key={occIdx} className={`p-2 ${occIdx === 0 ? 'bg-green-500/10 lg:bg-green-50' : ''}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-300 lg:text-gray-700 truncate">{occ.descrizione}</p>
                            <p className="text-xs text-slate-500 lg:text-gray-500">
                              {occ.quantita_fattura || 1} conf. × {occ.pezzi_confezione || 1} pz
                            </p>
                          </div>
                          <div className="text-right ml-2">
                            <p className={`font-bold ${occIdx === 0 ? 'text-green-400 lg:text-green-600' : 'text-cyan-400 lg:text-cyan-600'}`}>
                              €{(occ.prezzo_singolo_pezzo || 0).toFixed(2)}/pz
                            </p>
                            <p className="text-xs text-slate-500 lg:text-gray-500">
                              €{(occ.prezzo_confezione || 0).toFixed(2)}/conf.
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-1 text-xs">
                          <span className="bg-blue-500/20 lg:bg-blue-100 text-blue-300 lg:text-blue-700 px-2 py-0.5 rounded truncate max-w-[150px]">
                            {occ.fornitore}
                          </span>
                          <span className="text-slate-500 lg:text-gray-400">{occ.data_fattura}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl lg:rounded-xl p-4 lg:p-6 text-center transition-all ${
          dragOver 
            ? 'border-green-500 bg-green-500/10' 
            : 'border-slate-600 lg:border-gray-300 bg-slate-800/50 lg:bg-gray-50 hover:border-slate-500 lg:hover:border-gray-400'
        }`}
      >
        <Upload className={`h-8 w-8 lg:h-10 lg:w-10 mx-auto mb-2 ${dragOver ? 'text-green-500' : 'text-slate-400 lg:text-gray-400'}`} />
        <p className="text-xs lg:text-sm font-medium text-slate-200 lg:text-gray-700">Trascina qui i file XML</p>
        <p className="text-[10px] lg:text-xs text-slate-400 lg:text-gray-500 mt-1">.xml, .p7m, .zip • Upload multiplo</p>
      </div>
      
      {/* Year Selector */}
      {statisticheAnni.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 font-medium">Anno:</span>
          {statisticheAnni.map(anno => (
            <button
              key={anno.anno}
              onClick={() => setSelectedAnno(anno.anno)}
              className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-bold transition-all ${
                selectedAnno === anno.anno
                  ? 'bg-cyan-600 text-white shadow-lg'
                  : 'bg-slate-700 lg:bg-white text-slate-200 lg:text-gray-700 border border-slate-600 lg:border-gray-200'
              }`}
            >
              {anno.anno}
              <span className={`ml-1 ${selectedAnno === anno.anno ? 'text-cyan-100' : 'text-slate-400 lg:text-gray-400'}`}>
                ({anno.totale_fatture})
              </span>
            </button>
          ))}
        </div>
      )}
      
      {/* View Tabs */}
      <div className="flex gap-1 bg-slate-800 lg:bg-gray-100 p-1 rounded-xl lg:rounded-lg overflow-x-auto">
        {[
          { id: 'anni', icon: Calendar, label: 'Anni' },
          { id: 'list', icon: FileText, label: 'Lista' },
          { id: 'categorie', icon: PieChart, label: 'Cat.' },
          { id: 'fornitori', icon: Truck, label: 'Forn.' },
          { id: 'prodotti', icon: Package, label: 'Prod.' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
              view === tab.id ? 'bg-slate-700 lg:bg-white shadow text-cyan-400 lg:text-cyan-600' : 'text-slate-400 lg:text-gray-600'
            }`}
          >
            <tab.icon className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content based on view */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-400 lg:text-cyan-600" />
        </div>
      ) : (
        <>
          {/* ANNI VIEW */}
          {view === 'anni' && (
            <div className="space-y-3">
              {statisticheAnni.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-8 text-center">
                  <FileSearch className="h-10 w-10 text-slate-500 lg:text-gray-300 mx-auto mb-3" />
                  <p className="text-slate-200 lg:text-gray-600 font-medium text-sm">Nessuna fattura importata</p>
                  <p className="text-xs text-slate-400 lg:text-gray-500 mt-1">Carica i file XML o ZIP</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                  {statisticheAnni.map(anno => (
                    <div 
                      key={anno.anno} 
                      className={`bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border cursor-pointer transition-all hover:shadow-lg p-4 ${
                        selectedAnno === anno.anno ? 'ring-2 ring-cyan-500 border-cyan-500/50' : 'border-slate-700/50 lg:border-gray-200'
                      }`}
                      onClick={() => { setSelectedAnno(anno.anno); setView('list'); }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-2xl font-bold text-white lg:text-gray-900">{anno.anno}</span>
                        <span className="text-xs bg-cyan-500/20 lg:bg-cyan-100 text-cyan-400 lg:text-cyan-700 px-2 py-1 rounded-full">
                          {anno.totale_fatture} fatture
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Totale</span>
                          <span className="font-bold text-emerald-300 lg:text-green-600">{formatCurrency(anno.totale_importo)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Imponibile</span>
                          <span className="font-medium text-slate-200 lg:text-gray-700">{formatCurrency(anno.totale_imponibile)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">IVA</span>
                          <span className="font-medium text-orange-300 lg:text-orange-600">{formatCurrency(anno.totale_iva)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-700/50 lg:border-gray-200">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Fornitori</span>
                          <span className="font-medium text-slate-200 lg:text-gray-700">{anno.num_fornitori}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* CATEGORIE VIEW */}
          {view === 'categorie' && (
            <div className="space-y-4 overflow-x-hidden">
              {statisticheCategorie.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <PieChart className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessuna categoria trovata</p>
                  <p className="text-xs text-slate-500 mt-2">Usa "AI Cat." per categorizzare le fatture</p>
                </div>
              ) : (
                <>
                  {/* KPI Summary */}
                  {(() => {
                    const totaleImporto = statisticheCategorie.reduce((sum, c) => sum + c.totale_importo, 0);
                    const totaleFatture = statisticheCategorie.reduce((sum, c) => sum + c.totale_fatture, 0);
                    return (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Categorie</p>
                          <p className="text-lg font-bold text-white lg:text-gray-900">{statisticheCategorie.length}</p>
                        </div>
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Fatture</p>
                          <p className="text-lg font-bold text-cyan-300 lg:text-cyan-600">{totaleFatture}</p>
                        </div>
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Totale</p>
                          <p className="text-sm font-bold text-emerald-300 lg:text-green-600">
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totaleImporto)}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Categorie List */}
                  <div className="space-y-2">
                    {(() => {
                      const maxImporto = Math.max(...statisticheCategorie.map(c => c.totale_importo));
                      const totaleImporto = statisticheCategorie.reduce((sum, c) => sum + c.totale_importo, 0);
                      const sortedCategorie = [...statisticheCategorie].sort((a, b) => b.totale_importo - a.totale_importo);
                      
                      return sortedCategorie.map((cat, idx) => {
                        const colors = getCatColor(cat.categoria);
                        const percentualeMax = (cat.totale_importo / maxImporto * 100).toFixed(0);
                        const percentualeTotale = (cat.totale_importo / totaleImporto * 100).toFixed(1);
                        
                        return (
                          <div 
                            key={idx} 
                            className={`bg-slate-800/90 lg:bg-white rounded-2xl border ${colors.border} lg:border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg`}
                          >
                            <div
                              className="p-4 cursor-pointer hover:bg-slate-700/30 lg:hover:bg-gray-50 transition-colors"
                              onClick={() => setExpandedCategorie(prev => ({...prev, [cat.categoria]: !prev[cat.categoria]}))}
                            >
                              <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className={`w-10 h-10 rounded-xl ${colors.bg}/20 flex items-center justify-center text-lg`}>
                                    {colors.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-bold text-white lg:text-gray-900 block truncate">{cat.categoria || 'Non categorizzato'}</span>
                                    <span className="text-xs text-slate-400 lg:text-gray-500">{cat.totale_fatture} fatture · {percentualeTotale}% del totale</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <span className={`font-bold text-lg ${colors.text}`}>{formatCurrency(cat.totale_importo)}</span>
                                  {expandedCategorie[cat.categoria] ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                                </div>
                              </div>
                              
                              <div className="relative h-2 bg-slate-700/50 lg:bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`absolute left-0 top-0 h-full ${colors.bg} rounded-full transition-all duration-500`}
                                  style={{ width: `${percentualeMax}%` }}
                                />
                              </div>
                            </div>
                            
                            {expandedCategorie[cat.categoria] && (
                              <div className="border-t border-slate-700/50 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-50 p-4">
                                {cat.sottocategorie?.length > 0 ? (
                                  <>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-3 font-semibold">Sottocategorie</p>
                                    <div className="flex flex-wrap gap-2">
                                      {cat.sottocategorie.map((sub, subIdx) => (
                                        <button 
                                          key={subIdx}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ ...prev, categoria: cat.categoria, sottocategoria: sub.nome }));
                                            setView('list');
                                          }}
                                          className={`px-3 py-1.5 rounded-xl text-xs font-medium border bg-slate-700/50 lg:bg-white border-slate-600/50 lg:border-gray-200 text-slate-200 lg:text-gray-700 hover:scale-105 ${colors.text}`}
                                        >
                                          {sub.nome} <span className="opacity-60">({sub.count})</span>
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-xs text-slate-500 text-center py-2">Nessuna sottocategoria</p>
                                )}
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilters(prev => ({ ...prev, categoria: cat.categoria }));
                                    setView('list');
                                  }}
                                  className="mt-3 w-full text-center text-xs text-cyan-400 hover:text-cyan-300 py-1.5 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-colors"
                                >
                                  Vedi {cat.totale_fatture} fatture →
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* LIST VIEW */}
          {view === 'list' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-400 lg:text-gray-600">Fatture anno {selectedAnno}</div>
                {(filters.categoria || filters.sottocategoria) && (
                  <button
                    onClick={() => { setFilters({ fornitore: '', categoria: '', sottocategoria: '' }); dataCache.current = {}; loadFattureAnno(); }}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 px-2 py-1 bg-cyan-500/10 rounded-lg border border-cyan-500/30"
                  >
                    <X className="h-3 w-3" /> Rimuovi filtri
                  </button>
                )}
              </div>
              
              {(filters.categoria || filters.sottocategoria) && (
                <div className="flex flex-wrap gap-2">
                  {filters.categoria && (
                    <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                      Categoria: {filters.categoria}
                    </span>
                  )}
                  {filters.sottocategoria && (
                    <span className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded-full border border-cyan-500/30">
                      Sottocategoria: {filters.sottocategoria}
                    </span>
                  )}
                </div>
              )}
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                <Input
                  placeholder="Cerca per fornitore, numero fattura..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                />
              </div>
              
              {/* Summary Cards */}
              {(() => {
                const annoStats = statisticheAnni.find(a => a.anno === selectedAnno) || {};
                return (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-cyan-50 lg:to-cyan-100 border border-cyan-500/30 lg:border-cyan-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-cyan-300 lg:text-cyan-600 font-medium">Totale Fatture</p>
                      <p className="text-xl font-bold text-cyan-300 lg:text-cyan-900">{annoStats.totale_fatture || 0}</p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-green-50 lg:to-green-100 border border-green-500/30 lg:border-green-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-emerald-300 lg:text-green-600 font-medium">Importo Totale</p>
                      <p className="text-lg font-bold text-emerald-300 lg:text-green-900">{formatCurrency(annoStats.totale_importo || 0)}</p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Imponibile</p>
                      <p className="text-lg font-bold text-purple-300 lg:text-purple-900">{formatCurrency(annoStats.totale_imponibile || 0)}</p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">IVA</p>
                      <p className="text-lg font-bold text-orange-300 lg:text-orange-900">{formatCurrency(annoStats.totale_iva || 0)}</p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Fatture List - raggruppate per mese, dalla più recente */}
              {filteredFatture.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <FileSearch className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-200 lg:text-gray-600 font-medium">Nessuna fattura trovata</p>
                  <p className="text-sm text-slate-400 lg:text-gray-500 mt-1">Carica i file XML</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="fatture-lista-mensile">
                  {fattureMensili.map((gruppo) => (
                    <div key={gruppo.chiave} data-testid={`fatture-mese-${gruppo.chiave}`}>
                      <div className="flex items-center justify-between px-2 py-2 sticky top-0 z-10 bg-slate-900/95 lg:bg-gray-50/95 backdrop-blur-sm rounded-lg">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-cyan-500" />
                          <span className="font-semibold text-white lg:text-gray-800">{gruppo.label}</span>
                          <span className="text-xs text-slate-400 lg:text-gray-500">({gruppo.items.length} fatture)</span>
                        </div>
                        <span className="text-sm font-bold text-cyan-400 lg:text-cyan-700">{formatCurrency(gruppo.totale)}</span>
                      </div>
                      <div className="space-y-2 mt-1">
                        {gruppo.items.map((fattura) => (
                          <div 
                            key={fattura.id} 
                            className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 hover:shadow-md transition-shadow cursor-pointer p-3 lg:p-4"
                            onClick={() => viewFatturaDetail(fattura.id)}
                          >
                            <div className="flex flex-col sm:flex-row justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Building2 className="h-4 w-4 text-slate-400 lg:text-gray-400" />
                                  <span className="font-semibold text-white lg:text-gray-900">
                                    {fattura.fornitore?.denominazione || fattura.fornitore || 'Fornitore sconosciuto'}
                                  </span>
                                  {fattura.categoria && (
                                    <span className="text-xs px-2 py-0.5 bg-slate-700 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-full">
                                      {fattura.categoria}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-400 lg:text-gray-600">
                                  <span>N. {fattura.numero_fattura || fattura.numero}</span>
                                  <span className="font-medium">{formatDate(parseDataFattura(fattura.data || fattura.data_fattura))}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="font-bold text-lg text-white lg:text-gray-900">
                                    {formatCurrency(fattura.importo_totale || fattura.importo || 0)}
                                  </p>
                                  <p className="text-xs text-slate-400 lg:text-gray-500">
                                    {fattura.righe?.length || fattura.linee?.length || 0} articoli
                                  </p>
                                </div>
                                <Eye className="h-5 w-5 text-slate-400 lg:text-gray-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* FORNITORI VIEW */}
          {view === 'fornitori' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-cyan-50 lg:to-cyan-100 border border-cyan-500/30 lg:border-cyan-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-cyan-300 lg:text-cyan-600 font-medium">Totale Fornitori</p>
                  <p className="text-xl font-bold text-cyan-300 lg:text-cyan-900">{statisticheFornitori.length}</p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-emerald-50 lg:to-emerald-100 border border-emerald-500/30 lg:border-emerald-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-emerald-300 lg:text-emerald-600 font-medium">Valore Totale</p>
                  <p className="text-lg font-bold text-emerald-300 lg:text-emerald-900">
                    {formatCurrency(statisticheFornitori.reduce((sum, f) => sum + (f.totale_importo || 0), 0))}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Totale Fatture</p>
                  <p className="text-xl font-bold text-purple-300 lg:text-purple-900">
                    {statisticheFornitori.reduce((sum, f) => sum + (f.totale_fatture || 0), 0)}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">Media per Fornitore</p>
                  <p className="text-lg font-bold text-orange-300 lg:text-orange-900">
                    {formatCurrency(statisticheFornitori.length > 0 
                      ? statisticheFornitori.reduce((sum, f) => sum + (f.totale_importo || 0), 0) / statisticheFornitori.length 
                      : 0)}
                  </p>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                <Input
                  placeholder="Cerca fornitore..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                />
              </div>

              {statisticheFornitori.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <Truck className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessun fornitore trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {statisticheFornitori
                    .filter(f => !searchTerm || f.fornitore?.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((fornitore, idx) => {
                      const maxImporto = Math.max(...statisticheFornitori.map(f => f.totale_importo || 0));
                      const percentuale = maxImporto > 0 ? ((fornitore.totale_importo || 0) / maxImporto * 100) : 0;
                      
                      return (
                        <div key={idx} className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                          <div
                            className="p-4 cursor-pointer hover:bg-slate-700/30 lg:hover:bg-gray-50 transition-colors"
                            onClick={() => toggleFornitore(fornitore.fornitore)}
                          >
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                    <Truck className="h-5 w-5 text-cyan-400 lg:text-cyan-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-bold text-white lg:text-gray-900 block truncate">{fornitore.fornitore || 'N/D'}</span>
                                    <div className="flex flex-wrap gap-2 mt-1 text-xs">
                                      <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                        {fornitore.totale_fatture} fatture
                                      </span>
                                      {fornitore.partita_iva && (
                                        <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-400 lg:text-gray-500 rounded-lg hidden sm:inline">
                                          P.IVA: {fornitore.partita_iva}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3 relative h-1.5 bg-slate-700/50 lg:bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="absolute left-0 top-0 h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${percentuale}%` }}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="text-right">
                                  <p className="font-bold text-xl text-emerald-400 lg:text-emerald-600">{formatCurrency(fornitore.totale_importo)}</p>
                                </div>
                                {expandedFornitori[fornitore.fornitore] ? (
                                  <ChevronUp className="h-5 w-5 text-slate-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
          
          {/* PRODOTTI VIEW */}
          {view === 'prodotti' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Totale Prodotti</p>
                  <p className="text-xl font-bold text-purple-300 lg:text-purple-900">{statisticheProdotti.length}</p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-emerald-50 lg:to-emerald-100 border border-emerald-500/30 lg:border-emerald-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-emerald-300 lg:text-emerald-600 font-medium">Valore Totale</p>
                  <p className="text-lg font-bold text-emerald-300 lg:text-emerald-900">
                    {formatCurrency(statisticheProdotti.reduce((sum, p) => sum + (p.importo_totale || 0), 0))}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-cyan-50 lg:to-cyan-100 border border-cyan-500/30 lg:border-cyan-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-cyan-300 lg:text-cyan-600 font-medium">Articoli Unici</p>
                  <p className="text-xl font-bold text-cyan-300 lg:text-cyan-900">{statisticheProdotti.length}</p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">Fatture Totali</p>
                  <p className="text-xl font-bold text-orange-300 lg:text-orange-900">
                    {statisticheProdotti.reduce((sum, p) => sum + (p.num_fatture || 0), 0)}
                  </p>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                <Input
                  placeholder="Cerca prodotto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                />
              </div>

              {statisticheProdotti.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <Package className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessun prodotto trovato</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {statisticheProdotti
                    .filter(p => !searchTerm || p.prodotto?.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((prodotto, idx) => (
                      <div key={idx} className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 p-4 hover:shadow-lg transition-shadow">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Package className="h-5 w-5 text-purple-400 lg:text-purple-600 flex-shrink-0" />
                              <span className="font-semibold text-white lg:text-gray-900 truncate">{prodotto.prodotto || 'N/D'}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs">
                              <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                {prodotto.num_fatture} fatture
                              </span>
                              <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                Qtà: {prodotto.quantita_totale}
                              </span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-lg text-emerald-400 lg:text-emerald-600">{formatCurrency(prodotto.importo_totale)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Modal Dettaglio Fattura */}
      {showDetail && selectedFattura && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowDetail(false)}>
          <div className="bg-slate-800 lg:bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-white lg:text-gray-900">
                  Fattura N. {selectedFattura.numero_fattura || selectedFattura.numero}
                </h2>
                <p className="text-sm text-slate-400 lg:text-gray-500">
                  {selectedFattura.fornitore?.denominazione || selectedFattura.fornitore}
                </p>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-slate-400 hover:text-white lg:hover:text-gray-900">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-slate-400 lg:text-gray-500">Data</p>
                <p className="font-medium text-white lg:text-gray-900">{formatDate(selectedFattura.data || selectedFattura.data_fattura)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 lg:text-gray-500">Importo Totale</p>
                <p className="font-bold text-emerald-400 lg:text-green-600">{formatCurrency(selectedFattura.importo_totale || selectedFattura.importo)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 lg:text-gray-500">Imponibile</p>
                <p className="font-medium text-white lg:text-gray-900">{formatCurrency(selectedFattura.imponibile)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 lg:text-gray-500">IVA</p>
                <p className="font-medium text-orange-400 lg:text-orange-600">{formatCurrency(selectedFattura.iva)}</p>
              </div>
            </div>
            
            {(selectedFattura.righe || selectedFattura.linee)?.length > 0 && (
              <div>
                <h3 className="font-semibold text-white lg:text-gray-900 mb-3">Righe Fattura</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(selectedFattura.righe || selectedFattura.linee).map((riga, idx) => (
                    <div key={idx} className="bg-slate-700/50 lg:bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between">
                        <span className="text-white lg:text-gray-900 flex-1">{riga.descrizione}</span>
                        <span className="font-medium text-emerald-400 lg:text-green-600">{formatCurrency(riga.prezzo_totale || riga.importo)}</span>
                      </div>
                      <div className="text-xs text-slate-400 lg:text-gray-500 mt-1">
                        Qtà: {riga.quantita} {riga.unita_misura || ''} × {formatCurrency(riga.prezzo_unitario)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal Risultati Upload */}
      {showUploadResults && uploadResults && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className={`p-6 ${uploadResults.caricati > 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : uploadResults.duplicati > 0 ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-red-500 to-pink-500'}`}>
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  {uploadResults.caricati > 0 ? (
                    <FileText className="h-6 w-6" />
                  ) : uploadResults.duplicati > 0 ? (
                    <Package className="h-6 w-6" />
                  ) : (
                    <X className="h-6 w-6" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold">Risultato Caricamento</h3>
                  <p className="text-white/80 text-sm">{uploadResults.totale_file || 0} file elaborati</p>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{uploadResults.caricati || 0}</div>
                  <div className="text-xs text-emerald-700 font-medium">Caricate</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{uploadResults.duplicati || 0}</div>
                  <div className="text-xs text-amber-700 font-medium">Duplicate</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{uploadResults.errori_dettagli?.length || 0}</div>
                  <div className="text-xs text-red-700 font-medium">Errori</div>
                </div>
              </div>
              
              {/* Dettagli errori se presenti */}
              {uploadResults.errori_dettagli?.length > 0 && (
                <div className="bg-red-50 rounded-xl p-4 max-h-40 overflow-y-auto">
                  <div className="text-sm font-medium text-red-800 mb-2">Dettagli errori:</div>
                  <ul className="text-xs text-red-700 space-y-1">
                    {uploadResults.errori_dettagli.slice(0, 10).map((err, i) => (
                      <li key={i} className="truncate">• {err}</li>
                    ))}
                    {uploadResults.errori_dettagli.length > 10 && (
                      <li className="text-red-500 font-medium">... e altri {uploadResults.errori_dettagli.length - 10} errori</li>
                    )}
                  </ul>
                </div>
              )}
              
              {/* Dettagli duplicati se presenti */}
              {uploadResults.duplicati > 0 && uploadResults.duplicati_dettagli?.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 max-h-40 overflow-y-auto">
                  <div className="text-sm font-medium text-amber-800 mb-2">Fatture già presenti:</div>
                  <ul className="text-xs text-amber-700 space-y-1">
                    {uploadResults.duplicati_dettagli.slice(0, 5).map((dup, i) => (
                      <li key={i} className="truncate">• {dup.replace(': Fattura già presente', '')}</li>
                    ))}
                    {uploadResults.duplicati_dettagli.length > 5 && (
                      <li className="text-amber-600 font-medium">... e altre {uploadResults.duplicati_dettagli.length - 5}</li>
                    )}
                  </ul>
                </div>
              )}
              
              {/* Messaggio */}
              <div className="text-center text-gray-600 text-sm">
                {uploadResults.message}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 pb-6">
              <Button 
                onClick={() => setShowUploadResults(false)} 
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="close-upload-results"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantFattureElettroniche;
